import { CHARTER_COLORS as theme } from "../shared/controleTheme";
import { PRODUCTION_VARIANT, launchStatusLabel } from "../shared/controleVariants";
import { useState } from "react";
import { dossierLabel } from "./bisDossierData";
import { inputStyle } from "./bisLayoutStyles";
import {
    controlePatientPointCardStyle,
    controlePatientPointShellStyle,
    lastDossierPointInlineStyle,
    patientControlPointNumberStyle,
    patientLaunchMetaStyle,
    patientLaunchStatusCardStyle,
    sharedPatientCommentCardStyle,
    validationStatusPillStyle,
} from "./bisPatientStyles";
import { productionDossierBlockPillStyle } from "./bisProductionStyles";
import { formatDate, formatDateTime } from "../shared/controleFormat";
import { validationStatusColors } from "../shared/controleTheme";
import { isPatientPointTouched, normalizePatientPointState } from "../shared/patientControlRules";
import { buttonBase } from "../shared/styles/layoutStyles";
import { instructionInfoBadgeStyle, instructionTooltipStyle, lastDossierStatusStyle } from "../shared/styles/patientStyles";

export function PatientControlPointCard({ number, label, instruction, value, lastDossier = null, lastValue = null, lastComment = "", onChange, disabled = false }) {
    const hasBlockingIssue = isPatientPointTouched(value) && !value.validated;
    const [showInstruction, setShowInstruction] = useState(false);
    return (
        <div style={controlePatientPointShellStyle}>
            <div style={controlePatientPointCardStyle(value.validated, hasBlockingIssue)}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "28px",
                        height: "28px",
                        borderRadius: "999px",
                        ...patientControlPointNumberStyle(value.validated, hasBlockingIssue),
                        fontSize: "12px",
                        fontWeight: 900,
                        flexShrink: 0,
                    }}>
                        {number}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "7px", color: "#0f172a", fontSize: "14px", fontWeight: 900, lineHeight: 1.35 }}>
                            <span>{label}</span>
                            {instruction && (
                                <span style={{ position: "relative", display: "inline-flex" }}>
                                    <button
                                        type="button"
                                        aria-label="Afficher la marche à suivre"
                                        onMouseEnter={() => setShowInstruction(true)}
                                        onMouseLeave={() => setShowInstruction(false)}
                                        onFocus={() => setShowInstruction(true)}
                                        onBlur={() => setShowInstruction(false)}
                                        style={instructionInfoBadgeStyle}
                                    >
                                        i
                                    </button>
                                    {showInstruction && (
                                        <span role="tooltip" style={instructionTooltipStyle}>
                                            {instruction}
                                        </span>
                                    )}
                                </span>
                            )}
                        </div>
                        {hasBlockingIssue && (
                            <div style={{ color: "#b91c1c", fontSize: "12px", fontWeight: 800, marginTop: "4px" }}>
                                Remonté dans le dossier de lot.
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", color: "#334155", fontSize: "13px", fontWeight: 850, whiteSpace: "nowrap" }}>
                        <input
                            type="checkbox"
                            checked={value.validated}
                            disabled={disabled}
                            onChange={event => onChange({ validated: event.target.checked })}
                            style={{ width: "18px", height: "18px", accentColor: "#16a34a", cursor: disabled ? "not-allowed" : "pointer" }}
                        />
                        <span>Point validé</span>
                    </label>
                </div>
            </div>
            {lastDossier && <LastDossierPointInline dossier={lastDossier} value={lastValue} comment={lastComment} />}
        </div>
    );
}

export function SharedPatientControlComment({ value, onChange, disabled = false }) {
    return (
        <div style={sharedPatientCommentCardStyle}>
            <label style={{ display: "grid", gap: "6px" }}>
                <span style={{ color: "#0f172a", fontSize: "13px", fontWeight: 950 }}>
                    Commentaire
                </span>
                <textarea
                    value={value}
                    disabled={disabled}
                    onChange={event => onChange(event.target.value)}
                    placeholder="Commentaire commun"
                    style={{
                        ...inputStyle,
                        width: "100%",
                        minHeight: "76px",
                        resize: "vertical",
                        lineHeight: 1.4,
                        background: disabled ? "#f1f5f9" : "white",
                        cursor: disabled ? "not-allowed" : "text",
                    }}
                />
            </label>
        </div>
    );
}

export function LastDossierPointInline({ dossier, value, comment = "" }) {
    const state = normalizePatientPointState(value);
    return (
        <div style={lastDossierPointInlineStyle}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                <span style={{ color: "#1d4ed8", fontSize: "12px", fontWeight: 950 }}>
                    Dernier dossier · {dossier.numeroLot || "—"} · {formatDate(dossier.dateProduction)}
                </span>
                <span style={lastDossierStatusStyle(state.validated)}>
                    {state.validated ? "Validé" : "Non validé"}
                </span>
            </div>
            <div style={{ color: "#475569", fontSize: "12px", lineHeight: 1.4, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {comment?.trim() || state.comment?.trim() || "Aucun commentaire."}
            </div>
        </div>
    );
}

export function PatientLaunchStatusCard({ value, lastDossier = null, lastValue = null, onChange, disabled = false }) {
    const launchStatus = value.launchStatus || "PAS_LANCE";
    const launched = launchStatus === "LANCE";
    const lastLaunchStatus = lastValue?.launchStatus || "PAS_LANCE";
    return (
        <div style={controlePatientPointShellStyle}>
            <div style={patientLaunchStatusCardStyle(launched)}>
                <div style={{ minWidth: 0 }}>
                    <div style={{ color: "#0f172a", fontSize: "14px", fontWeight: 950 }}>
                        Statut de validation
                    </div>
                    <div style={{ marginTop: "3px", color: "#64748b", fontSize: "12px", fontWeight: 750 }}>
                        Point 2 · Traçage validation pharmaceutique
                    </div>
                    {launched && (
                        <div style={patientLaunchMetaStyle}>
                            {formatDateTime(value.launchStatusAt) || "Date non renseignée"}
                            {" · "}
                            {value.launchStatusBy || "Opérateur non renseigné"}
                        </div>
                    )}
                </div>
                <select
                    value={launchStatus}
                    disabled={disabled}
                    onChange={event => onChange(event.target.value)}
                    style={{
                        ...inputStyle,
                        ...validationStatusColors(launchStatus),
                        width: "150px",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.7 : 1,
                        fontWeight: 900,
                    }}
                >
                    <option value="PAS_LANCE">Pas validé</option>
                    <option value="LANCE">Validé</option>
                </select>
            </div>
            {lastDossier && (
                <div style={lastDossierPointInlineStyle}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", flexWrap: "wrap" }}>
                        <span style={{ color: "#1d4ed8", fontSize: "12px", fontWeight: 950 }}>
                            Dernier dossier · {dossierLabel(lastDossier)}
                        </span>
                        <span style={validationStatusPillStyle(lastLaunchStatus)}>
                            {launchStatusLabel(PRODUCTION_VARIANT, lastLaunchStatus)}
                        </span>
                    </div>
                    {(lastValue?.launchStatusAt || lastValue?.launchStatusBy) && (
                        <div style={{ color: "#475569", fontSize: "12px", lineHeight: 1.4 }}>
                            {formatDateTime(lastValue.launchStatusAt) || "Date non renseignée"}
                            {lastValue.launchStatusBy ? ` · ${lastValue.launchStatusBy}` : ""}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export function ControlPointCard({ number, color, label, action, complete = false }) {
    return (
        <div style={{
            background: "white",
            border: `1px solid ${theme.border}`,
            borderLeft: `4px solid ${color}`,
            borderRadius: "8px",
            padding: "14px 18px",
            boxShadow: "0 1px 2px rgba(12, 59, 60, 0.04)",
        }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                    <span style={{
                        display: "inline-flex",
                        width: "28px",
                        height: "28px",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "999px",
                        background: complete ? theme.accent : "#e8f1ef",
                        color: complete ? "white" : theme.primary,
                        border: `1px solid ${theme.border}`,
                        fontWeight: 900,
                        fontSize: "12px",
                        flexShrink: 0,
                    }}>
                        {number}
                    </span>
                    <div style={{ color: theme.ink, fontSize: "14px", fontWeight: 850, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                        {label}
                    </div>
                </div>
                <span style={productionDossierBlockPillStyle(complete)}>
                    {complete ? "Renseigné" : "À compléter"}
                </span>
            </div>
            {action && (
                <div style={{
                    marginTop: "10px",
                    paddingTop: "10px",
                    borderTop: `1px solid ${theme.border}`,
                }}>
                    {action}
                </div>
            )}
        </div>
    );
}

export function PointTwoComment({ value, validation = {}, pointColor, onChange, onValidationChange, disabled = false }) {
    return (
        <CommentValidationBlock
            value={value}
            validation={validation}
            onChange={onChange}
            onValidationChange={onValidationChange}
            validateLabel="Valider l'import"
            unvalidateLabel="Dévalider l'import"
            disabled={disabled}
            style={{
                padding: "10px",
                borderRadius: "8px",
                background: "#f8fafc",
                border: `1px solid ${pointColor}`,
            }}
        />
    );
}

export function CommentValidationBlock({
    value,
    validation = {},
    onChange,
    onValidationChange,
    validateLabel,
    unvalidateLabel,
    disabled = false,
    style = {},
}) {
    const validated = Boolean(validation?.validatedAt);
    return (
        <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 0.55fr)",
            gap: "12px",
            alignItems: "stretch",
            width: "100%",
            ...style,
        }}>
            <label style={{ display: "grid", gap: "6px", minWidth: 0 }}>
                <span style={{ color: "#0f172a", fontSize: "13px", fontWeight: 900 }}>
                    Commentaire
                </span>
                <textarea
                    style={{
                        ...inputStyle,
                        width: "100%",
                        minHeight: "76px",
                        resize: "vertical",
                        lineHeight: 1.45,
                        background: disabled ? "#f1f5f9" : "white",
                        cursor: disabled ? "not-allowed" : "text",
                    }}
                    value={value}
                    disabled={disabled}
                    onChange={event => onChange(event.target.value)}
                    placeholder="Commentaire"
                />
            </label>
            <div style={{
                display: "grid",
                alignContent: "stretch",
                gap: "8px",
                minHeight: "76px",
                padding: "10px",
                borderRadius: "8px",
                border: validated ? "1px solid #86efac" : "1px solid #cbd5e1",
                background: validated ? "#f0fdf4" : "white",
            }}>
                <button
                    type="button"
                    onClick={() => onValidationChange?.(!validated)}
                    disabled={disabled}
                    style={{
                        ...buttonBase,
                        minHeight: "34px",
                        background: validated ? "#dcfce7" : "#0f172a",
                        borderColor: validated ? "#86efac" : "#0f172a",
                        color: validated ? "#166534" : "white",
                        fontWeight: 950,
                        opacity: disabled ? 0.6 : 1,
                        cursor: disabled ? "not-allowed" : "pointer",
                    }}
                >
                    {validated ? unvalidateLabel : validateLabel}
                </button>
                <div style={{ color: validated ? "#166534" : "#64748b", fontSize: "11px", fontWeight: 800, lineHeight: 1.35 }}>
                    {validated ? (
                        <>
                            <div>{formatDateTime(validation.validatedAt)}</div>
                            <div>{validation.validatedBy || "Utilisateur non renseigné"}</div>
                        </>
                    ) : "Aucune validation enregistrée."}
                </div>
            </div>
        </div>
    );
}
